import React from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { amountFormat } from '../../commonLayouts/PropertyFormat/PropertyFormat';
import { buildProductPriceBreakdownRows } from '../../../utils/dealFinancing/buildProductPriceBreakdown';
import './ProductPriceBreakdownTooltip.css';

/**
 * Hyperlink shown beside Total Deal Value. On hover/focus/click it shows a tooltip with the
 * per-product unit price ($/MT) after early payment adjustment, followed by additional charges.
 * Hidden when there are no product rows to show.
 */
const ProductPriceBreakdownLink = ({ dealData, formatCurrency = amountFormat }) => {
    const { products, additionalCharges } = buildProductPriceBreakdownRows(dealData);

    if (products.length === 0) {
        return null;
    }

    const renderTooltip = (tooltipProps) => (
        <Tooltip {...tooltipProps} className='cust-tooltip productPriceBreakdownTooltip'>
            <table className='productPriceBreakdownTable font-b3'>
                <thead>
                    <tr className='border-bottom border-neutral-500'>
                        <th className='font-semiBold text-start'>Product Name</th>
                        <th className='font-semiBold text-end'>Unit Price ($/MT)</th>
                    </tr>
                </thead>
                <tbody>
                    {products.map((product) => (
                        <tr key={`${product.productName}-${product.unitPrice}`}>
                            <td className='text-start'>{product.productName}</td>
                            <td className='text-end'>
                                {product.unitPrice === null ? '\u2013' : formatCurrency(product.unitPrice)}
                            </td>
                        </tr>
                    ))}
                    {additionalCharges.length > 0 && (
                        <>
                            <tr>
                                <td
                                    colSpan={2}
                                    className='font-semiBold text-start pt-2 border-top border-neutral-500'>
                                    Additional Charges
                                </td>
                            </tr>
                            {additionalCharges.map((charge) => (
                                <tr key={`${charge.label}-${charge.amount}`}>
                                    <td className='text-start'>{charge.label}</td>
                                    <td className='text-end'>{formatCurrency(charge.amount)}</td>
                                </tr>
                            ))}
                        </>
                    )}
                </tbody>
            </table>
        </Tooltip>
    );

    return (
        <OverlayTrigger
            placement='auto'
            trigger={['hover', 'focus', 'click']}
            delay={{ show: 200, hide: 150 }}
            overlay={renderTooltip}>
            <span
                className='text-primary text-decoration-underline font-b4 cursor'
                role='button'
                tabIndex={0}
                aria-label='Unit Price Breakdown'>
                Unit Price Breakdown
            </span>
        </OverlayTrigger>
    );
};

export default ProductPriceBreakdownLink;
